import React, { useEffect, useMemo, useRef, useState } from "react"
import { GoogleMap as GoogleMaps, useJsApiLoader } from "@react-google-maps/api"

import { createAccountStore } from "@/stores/account"
import {
  useStadiumBoundaryListQuery,
  useStadiumByTeamAndPeriodQuery,
} from "@/hooks/api/useStadiumQuery"
import { createActivityStore } from "@/app/activity/store"
import { Stadium } from "@/types/stadium"

import { uniqWith } from "lodash"
import { GoogleMapProps, Latlng } from "@/app/activity/components/type"
import { Activity } from "@/types/activity"

import { colors } from "@/components/colors"

import "./googleMap.scss"

const containerStyle = {
  width: "100%",
  height: "500px",
}

const MAP_MAX_ZOOM = 20
const MAP_MIN_ZOOM = 8
const MAP_DEFAULT_ZOOM = 17
const MARKER_VISIBLE_MIN_ZOOM = 14

const MARKER_SIZE_LARGE = 40
const MARKER_SIZE_SMALL = 20

const polygonOptions = {
  strokeColor: colors.red["800"],
  strokeOpacity: 0.8,
  strokeWeight: 2,
  fillColor: colors.red["800"],
  fillOpacity: 0.35,
}

function GoogleMap({
  setCenter,
  setSearchValue,
  selectedStadium,
  center,
  setMap: setMapAction,
  editable,
  setSelectedStadium,
}: GoogleMapProps) {
  const infoWindow = useRef<google.maps.InfoWindow | null>(null)

  const markerPolygon = useRef<google.maps.Polygon | null>(null)

  const selectedStadiumPolygon = useRef<google.maps.Polygon | null>(null)

  const registerPolygon = useRef<google.maps.Polygon | null>(null)

  const rotationMarker = useRef<google.maps.marker.AdvancedMarkerElement | null>(null)

  const containerRef = useRef<HTMLDivElement | null>(null)
  const vertexObserverRef = useRef<MutationObserver | null>(null)
  const [mapBoundary, setMapBoundary] =
    useState<Record<"north" | "south" | "east" | "west", number>>()

  const mapZoom = useRef<number>(MAP_DEFAULT_ZOOM)
  const zoomValue = useMemo(() => {
    return mapZoom.current
  }, [mapZoom.current])

  const [isDragging, setIsDragging] = useState(false)
  const [isInit, setIsInit] = useState(true)
  const [isPolygonFlipped, setPolygonFlipped] = useState(false)

  const [polygonPaths, setPolygonPaths] = useState<Latlng[]>([])

  const [autoUndoCount, setAutoUndoCount] = useState(0)
  const [map, setMap] = React.useState<google.maps.Map | null>(null)

  const [markers, setMarkers] = useState<google.maps.marker.AdvancedMarkerElement[]>([])

  const cardinalOrder = ["southEast", "northEast", "northWest", "southWest"]

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAP_CREDENTIAL as string,
    language: window.navigator.language,
  })

  const { activity, setActivity } = createActivityStore((state) => ({
    activity: state.tempActivity,
    setActivity: state.setTempActivity,
  }))

  const { selectedIdfTeam } = createAccountStore((state) => ({
    selectedIdfTeam: state.selectedIdfTeam,
  }))

  const { data: recommendStadium } = useStadiumByTeamAndPeriodQuery({
    idfTeam: selectedIdfTeam as number,
    dateStart: new Date(activity?.dateStart as string).valueOf(),
    dateFinish: new Date(activity?.dateFinish as string).valueOf(),
  })

  const { data: stadiumListForMaker } = useStadiumBoundaryListQuery({
    minLat: mapBoundary?.south as number,
    maxLat: mapBoundary?.north as number,
    minLng: mapBoundary?.west as number,
    maxLng: mapBoundary?.east as number,
  })

  const onLoad = React.useCallback(function callback(map: google.maps.Map) {
    map.setOptions({
      mapId: import.meta.env.VITE_GOOGLE_MAP_ID as string,
      zoom: 17,
    })

    const bounds = new window.google.maps.LatLngBounds(center as google.maps.LatLngLiteral)
    map?.fitBounds(bounds)

    infoWindow.current = new google.maps.InfoWindow()

    setMap(map)
    setMapAction?.(map)

    setTimeout(() => {
      setIsInit(false)
    }, 100)
  }, [])

  const onUnmount = React.useCallback(function callback() {
    setMap(null)
    setMapAction?.(null)
  }, [])

  const zoomChangeHandler = () => {
    if (map && !isInit) {
      const zoom = map.getZoom() as number

      if (mapZoom.current !== zoom) {
        throttledBounceChange()
      }

      mapZoom.current = zoom
    }
  }

  const configMaker = () => {
    if (!stadiumListForMaker) {
      return
    }

    const markers = stadiumListForMaker?.map((stadium) => {
      const img = document.createElement("img")
      img.src = "/images/pin.png"
      img.style.width = `${MARKER_SIZE_LARGE}px`

      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: { lat: stadium.latitude as number, lng: stadium.longitude as number },
        map,
        content: img,
      })

      marker.content?.getRootNode().addEventListener("mouseover", () => {
        openMarkerTooltip(stadium, marker)
        visibleMarkerPolygon(stadium)
      })

      marker.content?.getRootNode().addEventListener("mouseout", () => {
        closeMarkerTooltip()
        hideMarkerPolygon()
      })

      marker.content?.getRootNode().addEventListener("click", () => {
        setActivity({ ...activity, idfStadium: stadium.idfStadium } as Activity)
      })

      return marker
    })

    setMarkers((prev) => uniqWith([...prev, ...markers], (a, b) => a.position === b.position))
  }

  const throttledBounceChange = () => setMapBoundary(map?.getBounds()?.toJSON())

  const makeRotationMarker = () => {
    if (rotationMarker.current) {
      rotateMarker()
    } else if (registerPolygon.current) {
      const img = document.createElement("img")
      img.src = "/images/map_marker_rotation.png"
      img.style.width = "40px"
      rotationMarker.current = new google.maps.marker.AdvancedMarkerElement({
        position: getRotationMarkerPath(),
        map,
        content: img,
        gmpDraggable: true,
      })
    }
  }

  const setRotationMarker = () => {
    makeRotationMarker()
    addRotationMarkerEventListener()
  }

  const getCenterLatLng = (path?: google.maps.MVCArray<google.maps.LatLng>) => {
    const bounds = new google.maps.LatLngBounds()
    const paths = (path ||
      registerPolygon.current?.getPath()) as google.maps.MVCArray<google.maps.LatLng>

    paths.forEach((latlng) => bounds.extend(new google.maps.LatLng(latlng.lat(), latlng.lng())))

    return bounds.getCenter()
  }

  const openMarkerTooltip = (
    stadium: Stadium,
    anchor: google.maps.marker.AdvancedMarkerElement,
  ) => {
    infoWindow.current?.setContent(`<div class="googleMap__marker--info">${stadium.name}</div>`)
    infoWindow.current?.open({ map, anchor, shouldFocus: false })
  }

  const closeMarkerTooltip = () => {
    infoWindow.current?.close()
  }

  const visibleMarkerPolygon = (stadium: Stadium) => {
    const polygonPaths = cardinalOrder.map((_, index) => {
      const { latitude, longitude } = (stadium.corners as Latlng[])[index]

      return new google.maps.LatLng(latitude, longitude)
    })

    if (!markerPolygon.current) {
      initMarkerPolygon(polygonPaths)
    } else {
      markerPolygon.current.setPaths(polygonPaths)
    }
    markerPolygon.current?.setVisible(true)
  }

  const hideMarkerPolygon = () => {
    markerPolygon.current?.setVisible(false)
  }

  const initMarkerPolygon = (polygonPaths: google.maps.LatLng[]) => {
    if (markerPolygon.current) {
      ;(markerPolygon.current as google.maps.Polygon).setMap(null)
    }

    markerPolygon.current = new google.maps.Polygon({
      paths: polygonPaths,
      visible: false,
      draggable: false,

      ...polygonOptions,
    })

    markerPolygon.current.setMap(map)
  }

  const setSelectedStadiumPolygon = (stadium?: Stadium) => {
    if (selectedStadiumPolygon.current) {
      selectedStadiumPolygon.current.setMap(null)
    }

    if (!stadium || !stadium.corners || stadium.corners.length < 4 || !google.maps.Polygon) {
      return
    }

    const paths = buildPolygonPaths(stadium.corners as Latlng[])

    selectedStadiumPolygon.current = new google.maps.Polygon({
      paths,
      ...polygonOptions,
    })

    selectedStadiumPolygon.current?.setMap(map)
  }

  const buildPolygonPaths = (corners: Latlng[]) => {
    if (!corners || corners.length < 4 || !google.maps.LatLng) {
      return []
    }

    const targetCoordinates = cardinalOrder.reduce(
      (polygonCoordinates, order, index) => {
        if (!corners.length) {
          return polygonCoordinates
        }
        const { latitude, longitude } = corners[index]
        polygonCoordinates[order] = { latitude, longitude }

        return polygonCoordinates
      },
      {} as Record<string, Latlng>,
    )

    return cardinalOrder.map(
      (direction) =>
        new google.maps.LatLng(
          targetCoordinates[direction].latitude,
          targetCoordinates[direction].longitude,
        ),
    )
  }

  const updateMap = async (stadium?: Stadium) => {
    const value = stadium || selectedStadium

    if (!value) {
      return
    }

    map?.setZoom(mapZoom.current)

    if ((value.corners as Latlng[]).length >= 4) {
      setRegisterPolygon(value.corners as Latlng[])
    }
    if (!editable) {
      return
    }

    const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))
    await sleep(200)

    addPolygonEventListener()
    setRotationMarker()
    hideVertex()
    startVertexObserver()
  }

  const hideVertex = () => {
    const otherVertex = containerRef.current?.querySelectorAll("div[style*='opacity: 0.5']")

    if (otherVertex) {
      otherVertex.forEach((vertex) => {
        vertex.setAttribute("style", "display: none")
        // 중간점의 부모 요소도 pointer-events 차단
        const parent = vertex.parentNode as HTMLElement
        if (parent) {
          parent.style.pointerEvents = "none"
        }
      })
    }
  }

  const startVertexObserver = () => {
    if (vertexObserverRef.current) {
      vertexObserverRef.current.disconnect()
    }

    if (!containerRef.current || !editable) {
      return
    }

    vertexObserverRef.current = new MutationObserver(() => {
      hideVertex()
    })

    vertexObserverRef.current.observe(containerRef.current, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style"],
    })
  }

  const stopVertexObserver = () => {
    if (vertexObserverRef.current) {
      vertexObserverRef.current.disconnect()
      vertexObserverRef.current = null
    }
  }

  const setRegisterPolygon = (corners?: Latlng[]) => {
    if (registerPolygon.current) {
      registerPolygon.current.setMap(null)
    }

    if (!corners) {
      return
    }

    const paths = buildPolygonPaths(corners)
    registerPolygon.current = new google.maps.Polygon({
      paths,
      ...polygonOptions,
      draggable: editable,
      editable: editable,
      clickable: true,
    })

    registerPolygon.current?.setMap(map)
  }

  const rotatePolygon = (e?: google.maps.MapMouseEvent) => {
    const getCenterLng = () => {
      const bounds = new google.maps.LatLngBounds()

      registerPolygon.current
        ?.getPath()
        .forEach((latlng) => bounds.extend(new google.maps.LatLng(latlng.lat(), latlng.lng())))

      return bounds.getCenter()
    }

    const { lat, lng } = getRotationMarkerPath()
    const { lat: centerLat, lng: centerLng } = getCenterLng()

    setCenter({
      lat: centerLat(),
      lng: centerLng(),
    })

    const prevLat = lat - centerLat()
    const prevLng = lng - centerLng()
    const nextLat = (e?.latLng?.lat() || 0) - centerLat()
    const nextLng = (e?.latLng?.lng() || 0) - centerLng()
    const angle = Math.atan2(
      prevLat * nextLng - prevLng * nextLat,
      prevLat * nextLat + prevLng * nextLng,
    )
    const projection = map?.getProjection()
    const paths = registerPolygon.current
      ?.getPath()
      .getArray()
      .map((latlng) => {
        const origin = projection?.fromLatLngToPoint(
          new google.maps.LatLng(centerLat() as number, centerLng() as number),
        )
        const point = projection?.fromLatLngToPoint(latlng)
        const rotatePoint = {
          x:
            Math.cos(angle) * ((point?.x as number) - (origin?.x as number)) -
            Math.sin(angle) * ((point?.y as number) - (origin?.y as number)) +
            (origin?.x as number),
          y:
            Math.sin(angle) * ((point?.x as number) - (origin?.x as number)) +
            Math.cos(angle) * ((point?.y as number) - (origin?.y as number)) +
            (origin?.y as number),
        }

        return projection?.fromPointToLatLng(rotatePoint as google.maps.Point) as google.maps.LatLng
      })

    const newPaths = registerPolygon.current?.getPath()
    newPaths?.clear()
    paths?.forEach((path) => newPaths?.push(path))
  }

  const getRotationMarkerPath = () => {
    const path = registerPolygon.current?.getPath() as google.maps.MVCArray<google.maps.LatLng>
    const lat = (path.getAt(0).lat() + path.getAt(1).lat()) / 2
    const lng = (path.getAt(0).lng() + path.getAt(1).lng()) / 2
    const center = getCenterLatLng(path)

    const result = {
      lat: 2 * lat - center.lat(),
      lng: 2 * lng - center.lng(),
    }

    return result
  }

  const addPolygonEventListener = () => {
    google.maps.event.addListener(
      registerPolygon.current?.getPath() || [],
      "set_at",
      updatePolygonVertex,
    )

    registerPolygon.current?.addListener("mouseover", () => {
      if (editable && map) {
        map.setOptions({ draggable: false })
      }
    })

    registerPolygon.current?.addListener("mouseout", () => {
      if (editable && map && !isDragging) {
        map.setOptions({ draggable: true })
      }
    })

    registerPolygon.current?.addListener("drag", () => {
      setIsDragging(true)
      if (editable) {
        rotateMarker()
      }
    })

    registerPolygon.current?.addListener("dragend", () => {
      updatePolygon()
      hideVertex()

      if (editable) {
        rotateMarker()
        if (map) {
          map.setOptions({ draggable: true })
        }
      }

      setIsDragging(false)
    })
  }

  const addRotationMarkerEventListener = () => {
    rotationMarker.current?.addListener("drag", (e: google.maps.MapMouseEvent) => {
      rotatePolygon(e)
      rotateMarker()
      hideVertex()
    })

    rotationMarker.current?.addListener("dragend", () => {
      rotateMarker()
      updatePolygon()
      hideVertex()
    })
  }

  const updatePolygonVertex = (pathIndex: number) => {
    if ((registerPolygon.current?.getPath().getLength() || 0) < 4 || isDragging) {
      return
    }

    const polygonPaths = registerPolygon.current
      ?.getPath()
      .getArray()
      .map((latlng) => {
        return new google.maps.LatLng(latlng.lat(), latlng.lng())
      })

    makeRectangle(polygonPaths || [], pathIndex)
    hideVertex()

    if (isPolygonFlipped) {
      ;(
        containerRef.current?.querySelector(
          "img[src='https://maps.gstatic.com/mapfiles/undo_poly.png']",
        ) as HTMLElement
      )?.click()
    }

    setPolygonPaths(
      polygonPaths?.map((path) => ({ latitude: path.lat(), longitude: path.lng() })) || [],
    )
    rotateMarker()
  }

  const updatePolygon = () => {
    updateCenterLatLng()

    const corners = cardinalOrder.map((_, index) => {
      const [latitude, longitude] = registerPolygon.current
        ?.getPath()
        .getAt(index)
        .toUrlValue()
        .split(",") as string[]

      return { latitude: parseFloat(latitude), longitude: parseFloat(longitude) }
    })

    setSelectedStadium?.({
      ...selectedStadium,
      corners: corners,
      latitude: center.lat,
      longitude: center.lng,
    } as Stadium)
  }

  const updateCenterLatLng = () => {
    const bounds = new google.maps.LatLngBounds()

    registerPolygon.current
      ?.getPath()
      .forEach((latlng) => bounds.extend(new google.maps.LatLng(latlng.lat(), latlng.lng())))
  }

  const rotateMarker = () => {
    if (rotationMarker.current) {
      rotationMarker.current.position = { ...getRotationMarkerPath() }
    }
  }

  const makeRectangle = (pathList: google.maps.LatLng[], movedPathIndex: number) => {
    const nearPointIndex = [(movedPathIndex + 1) % 4, (movedPathIndex + 3) % 4]
    const diagonalPointIndex = (movedPathIndex + 2) % 4

    const currentPoint = pathList[movedPathIndex]
    const nearPoints = [pathList[nearPointIndex[0]], pathList[nearPointIndex[1]]]
    const diagonalPoint = pathList[diagonalPointIndex]

    const difference3 = [
      currentPoint.lat() - diagonalPoint.lat(),
      currentPoint.lng() - diagonalPoint.lng(),
    ]

    const difference1 = [
      nearPoints[0].lat() - diagonalPoint.lat(),
      nearPoints[0].lng() - diagonalPoint.lng(),
    ]
    const difference2 = [
      nearPoints[1].lat() - diagonalPoint.lat(),
      nearPoints[1].lng() - diagonalPoint.lng(),
    ]
    const difference1Length = Math.sqrt(Math.pow(difference1[0], 2) + Math.pow(difference1[1], 2))
    const difference2Length = Math.sqrt(Math.pow(difference2[0], 2) + Math.pow(difference2[1], 2))
    const unit1Difference = [difference1[0] / difference1Length, difference1[1] / difference1Length]
    const unit2Difference = [difference2[0] / difference2Length, difference2[1] / difference2Length]

    const unitDiffTransposeMatrix = [
      [unit1Difference[0], unit2Difference[0]],
      [unit1Difference[1], unit2Difference[1]],
    ]

    const inverseMatrix = matrix_invert(unitDiffTransposeMatrix)

    const unit1Coefficient =
      difference3[0] * inverseMatrix[0][0] + difference3[1] * inverseMatrix[0][1]
    const unit2Coefficient =
      difference3[0] * inverseMatrix[1][0] + difference3[1] * inverseMatrix[1][1]

    const newPoint1 = [
      unit1Coefficient * unit1Difference[0] + diagonalPoint.lat(),
      unit1Coefficient * unit1Difference[1] + diagonalPoint.lng(),
    ]
    const newPoint2 = [
      unit2Coefficient * unit2Difference[0] + diagonalPoint.lat(),
      unit2Coefficient * unit2Difference[1] + diagonalPoint.lng(),
    ]

    const latLng1 = new google.maps.LatLng(newPoint1[0], newPoint1[1])
    const latLng2 = new google.maps.LatLng(newPoint2[0], newPoint2[1])

    pathList[nearPointIndex[0]] = latLng1
    pathList[nearPointIndex[1]] = latLng2

    if (unit1Coefficient < 0 || unit2Coefficient < 0) {
      if (autoUndoCount === 0) {
        setAutoUndoCount(autoUndoCount + 1)
        setPolygonFlipped(true)
      } else {
        setAutoUndoCount(autoUndoCount - 1)
        if (autoUndoCount === 0) {
          setPolygonFlipped(false)
        }
      }
    }

    const newPaths = registerPolygon.current?.getPath()
    newPaths?.clear()
    pathList.forEach((path) => newPaths?.push(path))
  }

  const matrix_invert = (matrix: number[][]): number[][] => {
    const dimensionSize = matrix.length

    if (matrix.length !== matrix[0].length) {
      return [[]]
    }

    const identity: number[][] = Array.from({ length: dimensionSize }, (_, i) =>
      Array.from({ length: dimensionSize }, (_, j) => (i === j ? 1 : 0)),
    )
    const copied = matrix.map((row) => [...row])

    for (let idx = 0; idx < dimensionSize; idx += 1) {
      let matrixElement = copied[idx][idx]

      if (matrixElement === 0) {
        for (let innerIdx = idx + 1; innerIdx < dimensionSize; innerIdx += 1) {
          if (copied[innerIdx][idx] !== 0) {
            for (let copyIdx = 0; copyIdx < dimensionSize; copyIdx++) {
              matrixElement = copied[idx][copyIdx]
              copied[idx][copyIdx] = copied[innerIdx][copyIdx]
              copied[innerIdx][copyIdx] = matrixElement
              matrixElement = identity[idx][copyIdx]
              identity[idx][copyIdx] = identity[innerIdx][copyIdx]
              identity[innerIdx][copyIdx] = matrixElement
            }

            break
          }
        }
        matrixElement = copied[idx][idx]

        if (matrixElement === 0) {
          return [[]]
        }
      }

      for (let copyIdx = 0; copyIdx < dimensionSize; copyIdx++) {
        copied[idx][copyIdx] = copied[idx][copyIdx] / matrixElement
        identity[idx][copyIdx] = identity[idx][copyIdx] / matrixElement
      }

      for (let innerIdx = 0; innerIdx < dimensionSize; innerIdx++) {
        if (innerIdx === idx) {
          continue
        }

        matrixElement = copied[innerIdx][idx]

        for (let copyIdx = 0; copyIdx < dimensionSize; copyIdx++) {
          copied[innerIdx][copyIdx] -= matrixElement * copied[idx][copyIdx]
          identity[innerIdx][copyIdx] -= matrixElement * identity[idx][copyIdx]
        }
      }
    }

    return identity
  }

  useEffect(() => {
    if (isLoaded && map) {
      google.maps.importLibrary("marker")

      if (selectedStadium) {
        updateMap(selectedStadium)
        // editable 모드일 때는 selectedStadiumPolygon을 그리지 않음 (registerPolygon과 겹쳐서 드래그 방해)
        if (!editable) {
          setSelectedStadiumPolygon(selectedStadium)
        }
      }
    }
  }, [isLoaded, selectedStadium, map])

  useEffect(() => {
    if (recommendStadium) {
      setSelectedStadiumPolygon(recommendStadium)
      setSearchValue(recommendStadium?.name as string)
    }
  }, [recommendStadium])

  useEffect(() => {
    if (map && google.maps.marker) {
      configMaker()
    }
  }, [stadiumListForMaker, map])

  useEffect(() => {
    if (!stadiumListForMaker && map) {
      const bounds = map.getBounds()
      if (bounds) {
        setMapBoundary(bounds.toJSON())
      }
    }
  }, [stadiumListForMaker, map])

  useEffect(() => {
    if (!isDragging && polygonPaths) {
      setSelectedStadium?.({
        ...selectedStadium,
        corners: polygonPaths as Latlng[],
      } as Stadium)
    }
  }, [isDragging, polygonPaths])

  useEffect(() => {
    const zoom = map?.getZoom() as number
    const markerIconSize = zoom < MARKER_VISIBLE_MIN_ZOOM ? MARKER_SIZE_SMALL : MARKER_SIZE_LARGE
    markers.forEach((marker) => {
      if (marker.content) {
        ;(marker.content as HTMLImageElement).style.width = `${markerIconSize}px`
      }
    })
  }, [zoomValue])

  useEffect(() => {
    if (map && center.lat && center.lng) {
      map.panTo({ lat: center.lat, lng: center.lng })
    }
  }, [map, center.lat, center.lng])

  useEffect(() => {
    return () => {
      stopVertexObserver()
    }
  }, [])

  return (
    <div className="googleMapCont" ref={containerRef}>
      {isLoaded ? (
        <GoogleMaps
          mapContainerStyle={containerStyle}
          center={center.lat && center.lng ? (center as google.maps.LatLngLiteral) : undefined}
          options={{
            zoomControl: true,
            mapTypeControl: true,
            streetViewControl: false,
            mapTypeId: "hybrid",
            minZoom: MAP_MIN_ZOOM,
            maxZoom: MAP_MAX_ZOOM,
            zoom: zoomValue,
            tilt: 0,
            rotateControl: false,
          }}
          onLoad={onLoad}
          onUnmount={onUnmount}
          onDragEnd={throttledBounceChange}
          onZoomChanged={zoomChangeHandler}></GoogleMaps>
      ) : (
        <></>
      )}
    </div>
  )
}

export default React.memo(GoogleMap)
